/**
 * Hook to fetch the configuration the login screen needs before anyone has
 * authenticated: which auth providers to offer (per knob settings), and which
 * workspace this hostname belongs to.
 *
 * @module hooks/useAuthConfig
 */
import { useQuery } from '@tanstack/react-query';
import type { LoginHost } from '@chatsdet/types';
import { API_URL } from '../config';
import { toApiError } from '../lib/api-error';

interface AuthConfig {
  providers: {
    microsoft: { enabled: boolean };
  };
  host: LoginHost;
}

/**
 * The shape to fall back to for the *provider* half of this config, which is a
 * pure UI toggle and safe to guess conservatively.
 *
 * Deliberately no `host` here, and deliberately not used as `placeholderData`.
 * A placeholder that asserts `host: shared` is a lie with consequences: on a
 * per-org host it means the app renders the ordinary sign-in form — the exact
 * "sign-in that can never succeed" this feature exists to remove — for as long
 * as the query is unsettled, which includes the whole retry sequence of a
 * sustained outage. Callers get `undefined` until the host is actually known
 * and are expected to wait rather than assume.
 */
const FALLBACK_PROVIDERS: AuthConfig['providers'] = {
  microsoft: { enabled: false },
};

export function useAuthConfig() {
  return useQuery({
    queryKey: ['auth', 'config'],
    queryFn: async (): Promise<AuthConfig> => {
      const res = await fetch(`${API_URL}/auth/config`);
      // `toApiError` rather than a bare `Error` so a 4xx is classified as an
      // expected client failure: this query now runs on every page load for
      // every user, so an unclassified throw would turn one rate-limit or
      // deploy blip into a Sentry storm.
      if (!res.ok) {
        throw await toApiError(res);
      }
      const body = await res.json();
      // A missing `host` IS reachable during a rolling deploy: the bundle hits
      // CloudFront before the last old API task drains, so a new client can be
      // answered by an API that predates this field. Default it to the `shared`
      // the old API's behavior implied — distinct from the error case above,
      // because here the server answered successfully and simply has nothing to
      // say about the host.
      //
      // Every `LoginHost` field that reaches a sink must still be re-validated
      // at that sink (see `movedHostUrl`): this type is a network payload, and
      // the annotation alone guarantees nothing about it.
      return {
        providers: body.config?.providers ?? FALLBACK_PROVIDERS,
        host: body.config?.host ?? { kind: 'shared' },
      };
    },
    staleTime: 60_000,
  });
}
